import * as React from 'react';
import { useEffect, useState } from 'react';

import * as pluralize from 'pluralize';

import { Card, Checkbox, FormControlLabel, Grid, GridItem, GridRow, Radio, RadioGroup, Spacer, Typography } from '@emburse/ui-core';

import { ICardsGatewayIssuingModalValues } from '@Actions/cardsGatewayIssuingModalActions';

import { AbacusCurrency } from '@Shared/helpers';
import { ShippingOption, ShippingOptionServiceEnum } from '@Shared/interfaces/CardsGatewayInterfaces';

interface IShippingMethodFormProps {
  shippingMethods: Record<ShippingOptionServiceEnum, ShippingOption[]>;
  requireSignature: boolean;
  service: ShippingOptionServiceEnum;
  onChange: (update: Partial<ICardsGatewayIssuingModalValues>) => void;
}

let radioButtonRefs: Record<ShippingOptionServiceEnum, any> = {
  [ShippingOptionServiceEnum.Standard]: null,
  [ShippingOptionServiceEnum.Express]: null,
  [ShippingOptionServiceEnum.Priority]: null
};

export const ShippingMethodFormBase: React.FC<Partial<IShippingMethodFormProps>> = ({
  onChange,
  shippingMethods,
  requireSignature,
  service
}) => {
  const [shippingOptionWithRequireSignature, setShippingOptionWithRequireSignature] = useState({} as ShippingOption);

  useEffect(() => {
    const newShippingOption =
      shippingMethods && shippingMethods[service]?.find((method) => Boolean(method.requireDeliverySignatureFeeAmount));
    setShippingOptionWithRequireSignature(newShippingOption);
  }, [shippingMethods, service]);

  radioButtonRefs = {
    [ShippingOptionServiceEnum.Standard]: React.useRef(null),
    [ShippingOptionServiceEnum.Express]: React.useRef(null),
    [ShippingOptionServiceEnum.Priority]: React.useRef(null)
  };

  if (shippingMethods === undefined || requireSignature === undefined || service === undefined) {
    return <></>;
  }

  return (
    <Grid container spacing={2} data-testid="Shipping-Method-Form-Container">
      <Grid item xs={12}>
        <Typography variant="subtitle1" data-testid="Shipping-Method-Form-Title">
          Shipping Method
        </Typography>
      </Grid>

      {shippingMethods[ShippingOptionServiceEnum.Express]?.length > 0 && (
        <>
          <Grid item xs={12}>
            {renderShippingSelectorRadioCards(shippingMethods, service || ShippingOptionServiceEnum.Standard, onChange)}
          </Grid>

          {shippingOptionWithRequireSignature && (
            <Grid item xs={12}>
              {renderShippingRequireSignatureCheckbox(
                ['Priority', 'Express'].includes(shippingOptionWithRequireSignature.service),
                requireSignature,
                shippingOptionWithRequireSignature.requireDeliverySignatureFeeAmount || 0,
                onChange
              )}
            </Grid>
          )}
        </>
      )}
      <Spacer size={16} />
    </Grid>
  );
};

function handleCardClick(refs: Record<string, any>, serviceName: string) {
  refs[serviceName].current.click();
}
function renderShippingSelectorRadioCards(
  methods: Record<string, ShippingOption[]>,
  service: ShippingOptionServiceEnum,
  onChange: (update: Partial<ICardsGatewayIssuingModalValues>) => void
) {
  const shippingMethodList = [methods[ShippingOptionServiceEnum.Standard], methods[ShippingOptionServiceEnum.Express]];
  return (
    <RadioGroup
      defaultValue={service}
      data-testid="Shipping-Method-Form-Radio-Group"
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const changedValues = {
          shippingService: e.target.value
        } as Partial<ICardsGatewayIssuingModalValues>;

        if (e.target.value === 'Standard') {
          changedValues.shippingRequireSignature = false; // standard shipping does not support signature
        }
        onChange(changedValues);
      }}
    >
      {shippingMethodList.map((options, index) => {
        const method = options[0];
        const feeFormatted = new AbacusCurrency(method.baseFeeAmount || 0, method.baseFeeCurrency, true).toFormattedString();
        return (
          <div key={method.service}>
            <Card
              className="shipping-method-card"
              onClick={() => {
                handleCardClick(radioButtonRefs, method.service);
              }}
            >
              <GridRow spacing={2} justifyContent="space-between" alignItems="flex-start">
                <GridItem xs={10}>
                  <FormControlLabel
                    value={method.service}
                    control={<Radio />}
                    label={renderShippingOptionName(method)}
                    ref={radioButtonRefs[method.service]}
                  />
                  <Typography color="textSecondary" variant="body2" className="shipping-method-description">
                    {renderShippingOptionDescription(method)}
                  </Typography>
                </GridItem>
                <GridItem xs={2}>
                  <Typography style={{ marginTop: '9px' }}>{feeFormatted}</Typography>
                </GridItem>
              </GridRow>
            </Card>
            {/* Don't add spacer after last card */}
            {index + 1 < shippingMethodList.length && <Spacer size={16} />}
          </div>
        );
      })}
    </RadioGroup>
  );
}

function renderShippingOptionDescription(method: ShippingOption) {
  // Standard: 7-10 business days
  // Priority: 5-7 business days with real-time tracking and optional signature. Charge is non-refundable.

  if (method.service === 'Standard') {
    return method.estArrivalMinBusinessDays + ' - ' + method.estArrivalMaxBusinessDays + ' business days';
  } else if (method.service === 'Express') {
    let dateRangeText = '';

    if (method.estArrivalMinBusinessDays === method.estArrivalMaxBusinessDays) {
      // e.g. 2 business days
      dateRangeText = method.estArrivalMinBusinessDays + ' business ' + pluralize('day', method.estArrivalMinBusinessDays);
    } else {
      // e.g. 2 - 4 business days
      dateRangeText = method.estArrivalMinBusinessDays + ' - ' + method.estArrivalMaxBusinessDays + ' business days';
    }

    return dateRangeText + ' with real-time tracking and optional signature. Charge is non-refundable.';
  }
  return '';
}

function renderShippingOptionName(method: ShippingOption) {
  if (method.service === 'Standard') {
    return 'Standard';
  } else if (method.service === 'Express') {
    return 'Expedited';
  } else if (method.service === 'Priority') {
    return 'Priority';
  }
  return null;
}

function renderShippingRequireSignatureCheckbox(
  available: boolean = false,
  checked: boolean = false,
  fee: number = 0,
  onChange: (update: Partial<ICardsGatewayIssuingModalValues>) => void
) {
  const feeFormatted = new AbacusCurrency(fee, 'USD', true).toFormattedString();
  return (
    <>
      <label htmlFor={'card_shipping_require_signature'}>
        <Checkbox
          id="card_shipping_require_signature"
          color="secondary"
          key="require_signature"
          onChange={() => {
            onChange({
              shippingRequireSignature: available ? !checked : false
            });
          }}
          checked={checked}
        />
        <span>Require Signature (Extra charge of {feeFormatted})</span>
      </label>
    </>
  );
}

export const ShippingMethodForm = React.memo(ShippingMethodFormBase);
